import { useEffect, useState } from 'react';
import { Bell, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/use-auth';
import { usePush } from '@/hooks/use-push';

const FLAG = 'founderos_push_autoprompt_v1';

/**
 * Fresh-device push opt-in. On first authed visit we fire the native browser
 * permission prompt directly (Chrome/Edge/Firefox allow it without a gesture).
 * Browsers that require a user gesture (Safari) fall back to a small banner
 * whose button triggers the same prompt. Runs once per device.
 */
export default function AutoPushPrompt() {
  const { user } = useAuth();
  const push = usePush();
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    if (!user?.id || !push.configured || push.deviceEnabled) return;
    if (typeof Notification === 'undefined') return;
    let done = false;
    try { done = localStorage.getItem(FLAG) === '1'; } catch { /* ignore */ }
    if (Notification.permission === 'granted') {
      // Permission exists but this device isn't registered yet — register silently.
      if (!done) {
        try { localStorage.setItem(FLAG, '1'); } catch { /* ignore */ }
        push.enable().catch(() => {});
      }
      return;
    }
    if (done || Notification.permission === 'denied') {
      if (!done) { try { localStorage.setItem(FLAG, '1'); } catch { /* ignore */ } }
      return;
    }
    try { localStorage.setItem(FLAG, '1'); } catch { /* ignore */ }
    (async () => {
      try {
        await push.enable(); // triggers the native permission prompt directly
      } catch {
        if (Notification.permission === 'default') setShowBanner(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, push.configured]);

  if (!showBanner) return null;
  return (
    <div className="fixed bottom-5 right-5 z-50 max-w-xs rounded-2xl border border-border bg-card shadow-elevated p-4" data-testid="push-autoprompt-banner">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-xl bg-primary/10 text-primary grid place-items-center shrink-0"><Bell className="h-4 w-4" /></div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">Turn on notifications</p>
          <p className="text-xs text-muted-foreground mt-0.5">Get session reminders, mentor replies and deadline nudges on this device.</p>
        </div>
        <button type="button" className="text-muted-foreground hover:text-foreground shrink-0" onClick={() => setShowBanner(false)} data-testid="push-autoprompt-dismiss" aria-label="Dismiss">
          <X className="h-4 w-4" />
        </button>
      </div>
      <Button size="sm" className="mt-3 w-full rounded-full" data-testid="push-autoprompt-enable"
        onClick={async () => { try { await push.enable(); } catch { /* blocked */ } setShowBanner(false); }}>
        Enable notifications
      </Button>
    </div>
  );
}
